import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../state'
import * as shared from '../shared'

/** Modal for editing organization joint-use settings */
@ngCore.Component({
  imports: [
    ds.layout.widget.Component,
    ds.overlay.centered.Component,
    ds.button.Component,
    ds.input.select.single.Component,
    shared.functionalizations.Component,
  ],
  selector: 'atn-space-joint-use-modal',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.space.organizations.Collection} */
  public readonly organizationsEcSg = ngCore.input.required<
    ec.Collection<state.space.organizations.TypeParams>
  >({
    alias: 'organizationsEc',
  })

  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)

  /** The organization currently being edited */
  private readonly _organizationSg = ngCore.computed(
    (organizationsEc = this.organizationsEcSg()) =>
      organizationsEc.plugins.currentEntityPlugin.feature.selectors
        .getEditedEntity({
          target:
            state.space.organizations.plugins.jointUse.CURRENT_ENTITY_TARGET,
        })
        .asSignal(),
  )

  /** @see {@link state.space.organizations.plugins.jointUse.Plugin} */
  private readonly _jointUsePluginSg = ngCore.computed(
    (organizationsEc = this.organizationsEcSg()) =>
      organizationsEc.plugins
        .lookup(state.space.organizations.plugins.jointUse.Plugin)
        .get(),
  )

  /**
   * Whether the organization being edited is at the root entity level
   */
  private readonly _isRootOrganizationSg = ngCore.computed(
    (
      organization = this._organizationSg(),
      schema = this._schemaFeature.selectors.schema.asSignal(),
    ) =>
      TypeChecker.isNotNullish(organization?.entityId) &&
      schema.isRoot(organization.entityId),
  )

  /** The calculation mode currently selected in the editing state */
  public readonly currentCalculationModeSg = ngCore.computed(
    (plugin = this._jointUsePluginSg()) =>
      plugin.feature.selectors.calculationMode.asSignal(),
  )

  /** Whether organization editing is being submitted */
  public readonly isSubmittingSg = ngCore.computed(
    (organizationsEc = this.organizationsEcSg()) =>
      organizationsEc.plugins.editingPlugin.feature.selectors.isSubmitting.asSignal(),
  )

  /** Whether displayed functionalizations are loading */
  public readonly isLoadingFunctionalizationsSg = ngCore.computed(
    (plugin = this._jointUsePluginSg()) =>
      plugin.feature.selectors.isLoading.asSignal(),
  )

  /** Functionalizations for the current calculation mode */
  public readonly functionalizationsSg = ngCore.computed(
    (plugin = this._jointUsePluginSg()) =>
      plugin.feature.selectors.functionalizations.asSignal(),
  )

  /** Whether the selected calculation mode is manual */
  private readonly _isManualCalculationModeSg = ngCore.computed(
    (calculationMode = this.currentCalculationModeSg()) =>
      calculationMode !== undefined &&
      ents.extra.space.Organization.JointUseCalculationMode.isManual(
        calculationMode,
      ),
  )

  /** Whether displayed functionalizations can be edited */
  public readonly canEditFunctionalizationsSg = ngCore.computed(() =>
    this._isManualCalculationModeSg(),
  )

  /** Whether user can save */
  public readonly canConfirmSg = ngCore.computed(
    (
      isManualCalculationMode = this._isManualCalculationModeSg(),
      functionalizations = this.functionalizationsSg(),
    ) =>
      !isManualCalculationMode ||
      ents.extra.space.Functionalization.isFullyCoded(functionalizations),
  )

  /**
   * Calculation mode items for select. Excludes
   * {@link ents.extra.space.Organization.JointUseCalculationMode.Inherit} when
   * org is at root entity level
   */
  public readonly calculationModeItemsSg = ngCore.computed<
    ds.input.select.models.Item<ents.extra.space.Organization.JointUseCalculationMode>[]
  >(() => {
    const modes = this._isRootOrganizationSg()
      ? ents.extra.space.Organization.JointUseCalculationMode.ALL.filter(
          mode =>
            !ents.extra.space.Organization.JointUseCalculationMode.isInherit(
              mode,
            ),
        )
      : ents.extra.space.Organization.JointUseCalculationMode.ALL

    return modes.map(mode => ({
      label: mode,
      value: mode,
    }))
  })

  /** Update the edited organization's joint-use calculation mode */
  public updateJointUseCalculationMode(
    calculationMode:
      | ents.extra.space.Organization.JointUseCalculationMode
      | undefined,
  ): void {
    if (TypeChecker.isNullish(calculationMode)) {
      return
    }

    this._jointUsePluginSg()
      .feature.actions.updateCalculationMode.from(
        'Space.Organizations.JointUseModal.CalculationModeChanged',
      )
      .dispatch({ calculationMode })
  }

  /** Update one manual functionalization */
  public updateManualFunctionalization(
    functionalization: ents.extra.space.Functionalization,
  ): void {
    this._jointUsePluginSg()
      .feature.actions.updateManualFunctionalization.from(
        'Space.Organizations.JointUseModal.FunctionalizationChanged',
      )
      .dispatch(functionalization)
  }

  /** Save changes */
  public saveClickHandler(): void {
    this.organizationsEcSg()
      .plugins.editingPlugin.feature.actions.submit.from(
        'Space.Organizations.JointUseModal.Save.Clicked',
      )
      .dispatch({
        current: {
          target:
            state.space.organizations.plugins.jointUse.CURRENT_ENTITY_TARGET,
        },
      })
  }

  /** Cancel edit modal */
  public cancelClickHandler(): void {
    this.organizationsEcSg()
      .plugins.editingPlugin.feature.actions.discard.from(
        'Space.Organizations.JointUseModal.Cancel.Clicked',
      )
      .dispatch({
        current: {
          target:
            state.space.organizations.plugins.jointUse.CURRENT_ENTITY_TARGET,
        },
      })
  }
}
